class PartnersMap extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector('.js-search-input');
    this.countryFilter = this.querySelector('.js-country-filter');
    this.filterButtons = [...this.querySelectorAll('.js-location-filter')];
    this.locationsList = this.querySelector('.js-locations-list');
    this.noResultsMessage = this.querySelector('.js-no-results-message');
    this.resultsCount = this.querySelector('.js-results-count');
    this.allCount = this.querySelector('.js-all-count');
    this.locations = [...this.querySelectorAll('.js-location-item')];
    this.markers = [...this.querySelectorAll('gmp-advanced-marker')];
    this.mapElement = this.querySelector('.js-map');
    this.activeType = this.querySelector('.js-location-filter[aria-pressed="true"]')?.dataset.type || '';
    this.activeLocation = null;
    this.locateButton = this.querySelector('.js-locate-button');
    this.locateError = this.querySelector('.js-locate-error');
    this.userMarker = null;
    this.initialZoom = Number(this.mapElement?.getAttribute('zoom'));
    this.focusZoom = 14;
    this.focusOffset = 80;
    this.isLocating = false;
    this.sortedByDistance = false;
    this.handleSearchBound = this.handleSearch.bind(this);
    this.handleLocateBound = this.handleLocate.bind(this);
    this.handleCountryChangeBound = this.handleCountryChange.bind(this);
    this.handleFilterClickBound = this.handleFilterClick.bind(this);
    this.handleLocationClickBound = this.handleLocationClick.bind(this);
    this.handleMarkerClickBound = this.handleMarkerClick.bind(this);
    this.handlePopupCloseBound = this.handlePopupClose.bind(this);
  }

  connectedCallback() {
    this.searchInput?.addEventListener('input', this.handleSearchBound);
    this.countryFilter?.addEventListener('change', this.handleCountryChangeBound);
    this.querySelector('.js-location-filters')?.addEventListener('click', this.handleFilterClickBound);
    this.locationsList?.addEventListener('click', this.handleLocationClickBound);
    this.locateButton?.addEventListener('click', this.handleLocateBound);
    this.mapElement?.addEventListener('click', this.handlePopupCloseBound, true);
    this.markers.forEach((marker) => {
      marker.addEventListener('gmp-click', this.handleMarkerClickBound);
    });

    if (this.mapElement) {
      customElements.whenDefined('gmp-map').then(() => {
        this.mapElement.innerMap?.setOptions({ streetViewControl: false });
      });
    }
  }

  disconnectedCallback() {
    this.searchInput?.removeEventListener('input', this.handleSearchBound);
    this.countryFilter?.removeEventListener('change', this.handleCountryChangeBound);
    this.querySelector('.js-location-filters')?.removeEventListener('click', this.handleFilterClickBound);
    this.locationsList?.removeEventListener('click', this.handleLocationClickBound);
    this.locateButton?.removeEventListener('click', this.handleLocateBound);
    this.mapElement?.removeEventListener('click', this.handlePopupCloseBound, true);
    this.markers.forEach((marker) => {
      marker.removeEventListener('gmp-click', this.handleMarkerClickBound);
    });
  }

  handleLocationClick(event) {
    if (event.target.closest('.partners-map__route')) return;

    const location = event.target.closest('.js-location-item');
    if (!location || location.hidden) return;

    this.selectLocation(location);
  }

  handlePopupClose(event) {
    if (!event.target.closest('.partners-map__popup-close')) return;

    event.stopPropagation();
    this.clearActiveLocation();
  }

  handleMarkerClick(event) {
    if (event.domEvent?.target?.closest?.('.partners-map__popup-close')) return;

    const location = this.locations.find((item) => item.dataset.locationId === event.currentTarget.dataset.locationId);
    if (!location || location.hidden) return;

    this.selectLocation(location, { scroll: true });
  }

  selectLocation(location, { scroll = false } = {}) {
    this.activeLocation = location;

    this.locations.forEach((item) => {
      const isActive = item === location;
      item.classList.toggle('is-active', isActive);

      if (isActive) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });

    this.markers.forEach((marker) => {
      const isActive = marker.dataset.locationId === location.dataset.locationId;
      marker.classList.toggle('is-active', isActive);
      marker.zIndex = isActive ? 2 : 0;
    });

    if (scroll) location.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    this.focusMap(location);
  }

  clearActiveLocation() {
    this.activeLocation = null;
    this.locations.forEach((item) => {
      item.classList.remove('is-active');
      item.removeAttribute('aria-current');
    });
    this.markers.forEach((marker) => {
      marker.classList.remove('is-active');
      marker.zIndex = 0;
    });
  }

  focusMap(location) {
    const latitude = Number(location.dataset.latitude);
    const longitude = Number(location.dataset.longitude);
    if (!this.mapElement || Number.isNaN(latitude) || Number.isNaN(longitude)) return;

    const map = this.mapElement.innerMap;
    if (!map) return;

    const zoom = Math.max(map.getZoom() || 0, this.focusZoom);
    const showPopup = window.matchMedia('(min-width: 768px)').matches;
    const center = showPopup ? this.offsetCenter(latitude, longitude, zoom) : { lat: latitude, lng: longitude };

    if (typeof map.moveCamera === 'function') {
      map.moveCamera({ center, zoom });
      return;
    }

    map.panTo(center);
    if (map.getZoom() < this.focusZoom) map.setZoom(this.focusZoom);
  }

  offsetCenter(latitude, longitude, zoom) {
    const scale = 256 * 2 ** zoom;
    const pin = this.toWorldPoint(latitude, longitude);
    const center = this.fromWorldPoint(pin.x, pin.y - this.focusOffset / scale);

    return { lat: center.latitude, lng: longitude };
  }

  toWorldPoint(latitude, longitude) {
    const sin = Math.sin((latitude * Math.PI) / 180);
    const x = (longitude + 180) / 360;
    const y = 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);

    return { x, y };
  }

  fromWorldPoint(x, y) {
    const longitude = x * 360 - 180;
    const mercator = Math.PI - 2 * Math.PI * y;
    const latitude = (180 / Math.PI) * Math.atan(Math.sinh(mercator));

    return { latitude, longitude };
  }

  handleLocate() {
    if (this.isLocating) return;

    if (!navigator.geolocation) {
      console.error('Geolocation is not available');
      this.showLocateError();
      return;
    }

    this.hideLocateError();
    this.isLocating = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.isLocating = false;
        this.hideLocateError();
        this.sortByDistance(position.coords.latitude, position.coords.longitude);
        this.showUserLocation(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        this.isLocating = false;
        console.error(error);
        this.showLocateError(error.code);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  }

  showLocateError(code) {
    const locateError = this.querySelector('.js-locate-error');
    const text = locateError?.querySelector('.js-locate-error-text');
    if (!locateError || !text) return;

    const denied = code === 1;
    text.textContent = denied ? locateError.dataset.denied : locateError.dataset.failed;
    locateError.hidden = false;
    locateError.classList.add('is-visible');
  }

  hideLocateError() {
    const locateError = this.querySelector('.js-locate-error');
    if (!locateError) return;

    locateError.hidden = true;
    locateError.classList.remove('is-visible');
  }

  showUserLocation(latitude, longitude) {
    if (!this.mapElement) return;

    if (!this.userMarker) {
      const marker = document.createElement('gmp-advanced-marker');
      const dot = document.createElement('span');

      marker.className = 'partners-map__user-marker';
      marker.setAttribute('aria-hidden', 'true');
      dot.className = 'partners-map__user-dot';
      marker.append(dot);
      this.mapElement.append(marker);
      this.userMarker = marker;
    }

    this.userMarker.setAttribute('position', `${latitude}, ${longitude}`);
    this.centerOnUser(latitude, longitude);
  }

  centerOnUser(latitude, longitude) {
    const map = this.mapElement?.innerMap;
    if (!map || !Number.isFinite(this.initialZoom)) return;

    const center = { lat: latitude, lng: longitude };

    if (typeof map.moveCamera === 'function') {
      map.moveCamera({ center, zoom: this.initialZoom });
      return;
    }

    map.panTo(center);
    map.setZoom(this.initialZoom);
  }

  sortByDistance(latitude, longitude) {
    if (!this.locationsList) return;

    this.locations.forEach((location) => {
      const partnerLatitude = Number(location.dataset.latitude);
      const partnerLongitude = Number(location.dataset.longitude);
      const distanceLabel = location.querySelector('.partners-map__distance');
      const hasCoordinates = Number.isFinite(partnerLatitude) && Number.isFinite(partnerLongitude);

      if (!hasCoordinates) {
        delete location.dataset.distance;
        if (distanceLabel) {
          distanceLabel.textContent = '';
          distanceLabel.hidden = true;
        }
        return;
      }

      const distance = this.distanceInKm(latitude, longitude, partnerLatitude, partnerLongitude);
      location.dataset.distance = String(distance);

      if (distanceLabel) {
        distanceLabel.textContent = this.formatDistance(distance);
        distanceLabel.hidden = false;
      }
    });

    this.locations.sort((left, right) => {
      const leftDistance = left.dataset.distance;
      const rightDistance = right.dataset.distance;
      const leftMissing = leftDistance === undefined || leftDistance === '';
      const rightMissing = rightDistance === undefined || rightDistance === '';

      if (leftMissing && rightMissing) return 0;
      if (leftMissing) return 1;
      if (rightMissing) return -1;

      return Number(leftDistance) - Number(rightDistance);
    });

    this.locations.forEach((location) => this.locationsList.append(location));
    this.sortedByDistance = true;
    this.filterLocations();
  }

  distanceInKm(fromLatitude, fromLongitude, toLatitude, toLongitude) {
    const earthRadiusKm = 6371;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const latitudeDelta = toRadians(toLatitude - fromLatitude);
    const longitudeDelta = toRadians(toLongitude - fromLongitude);
    const fromLatitudeRadians = toRadians(fromLatitude);
    const toLatitudeRadians = toRadians(toLatitude);
    const haversine = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(fromLatitudeRadians) * Math.cos(toLatitudeRadians) * Math.sin(longitudeDelta / 2) ** 2;

    return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  formatDistance(kilometers) {
    const locale = document.documentElement.lang || undefined;
    const value = kilometers < 10
      ? kilometers.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      : Math.round(kilometers).toLocaleString(locale, { maximumFractionDigits: 0 });

    return `${value} km`;
  }

  handleSearch() {
    this.filterLocations();
  }

  handleCountryChange() {
    this.filterLocations();
  }

  handleFilterClick(event) {
    const filterButton = event.target.closest('.js-location-filter');
    if (!filterButton) return;

    this.activeType = filterButton.dataset.type || '';
    this.filterButtons.forEach((button) => {
      const isActive = button === filterButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    this.filterLocations();
  }

  filterLocations() {
    const query = this.searchInput?.value.trim().toLowerCase() || '';
    const country = this.countryFilter?.value || '';
    let visibleCount = 0;
    let typePoolCount = 0;

    this.locations.forEach((location) => {
      const searchText = location.dataset.search?.toLowerCase() || '';
      const matchesSearch = searchText.includes(query);
      const matchesCountry = country === '' || location.dataset.country === country;
      const matchesType = this.activeType === '' || location.dataset.type === this.activeType;
      const inTypePool = matchesSearch && matchesCountry;
      const matches = inTypePool && matchesType;
      const marker = this.markers.find((item) => item.dataset.locationId === location.dataset.locationId);

      location.hidden = !matches;
      marker?.classList.toggle('is-hidden', !matches);

      if (inTypePool) typePoolCount += 1;
      if (matches) visibleCount += 1;
    });

    if (this.allCount) {
      const allLabel = this.querySelector('.js-location-filter[data-type=""]')?.dataset.label || '';
      this.allCount.textContent = `${allLabel} (${typePoolCount})`;
    }

    if (this.resultsCount) {
      const label = this.resultsCount.dataset.label || '';
      const distanceLabel = this.sortedByDistance ? this.resultsCount.dataset.distanceLabel : '';
      const suffix = distanceLabel ? ` · ${distanceLabel}` : '';
      this.resultsCount.textContent = `${visibleCount} ${label}${suffix}`;
    }

    const filtersActive = query !== '' || country !== '' || this.activeType !== '';
    this.noResultsMessage?.toggleAttribute('hidden', !(filtersActive && visibleCount === 0));
    this.locationsList?.toggleAttribute('hidden', visibleCount === 0);

    if (this.activeLocation?.hidden) this.clearActiveLocation();
  }
}

if (!customElements.get('partners-map')) {
  customElements.define('partners-map', PartnersMap);
}
